'use client';

import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { isNative } from '@unifyapps/ui/utils/isNative';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import MatrixEndpoint from '../auth/context/native/matrixEndpoint';
import useFetchInstance from '../hooks/useFetchInstance';
import { useInsights } from '../hooks/useInsights';
import { AnalyticsContext } from './AnalyticsContext';
import { createClient, type UnifyAppsAnalyticsClient } from './analytics';
import { CLIENT_CONFIG } from './clientConfig';
import { getEventPayload } from './getEventPayload';
import { stopAnalyticsClient } from './stopAnalyticsClient';
import { useAnalytics } from './useAnalytics';

interface AnalyticsProviderProps {
  isEnabled: boolean;
  config?: Parameters<UnifyAppsAnalyticsClient['setup']>[0];
  getTrackingData?: typeof GET_DEFAULT_TRACKING_DATA;
}

function AnalyticsProviderContent({ children }: PropsWithChildren) {
  const { heartbeat } = useAnalytics();
  useInsights();

  useEffect(() => {
    heartbeat();
  }, [heartbeat]);

  return children;
}

const GET_DEFAULT_TRACKING_DATA = () => {
  return {
    componentId: isNative() ? '' : window.location.pathname,
  };
};

function AnalyticsProvider({
  children,
  isEnabled,
  config = CLIENT_CONFIG,
  getTrackingData,
}: PropsWithChildren<AnalyticsProviderProps>) {
  const [analyticsClient, setAnalyticsClient] = useState<UnifyAppsAnalyticsClient | undefined>(
    undefined,
  );

  const fetchInstance = useFetchInstance();
  const getTrackingDataFn = useEventCallback(() =>
    (getTrackingData ?? GET_DEFAULT_TRACKING_DATA)(),
  );

  useEffect(() => {
    if (!isEnabled || typeof createClient !== 'function') {
      setAnalyticsClient(undefined);
      return;
    }

    const unifyAppsClient: UnifyAppsAnalyticsClient = createClient({
      ...config,
      beforeEvent: getEventPayload(getTrackingDataFn),
      fetch: fetchInstance,
      // web: keep host empty so the request stays relative and goes through
      // useFetchInstance → executeFetch → prependBaseURL, which applies
      // meta.basePath. Passing window.location.origin would short-circuit
      // getBaseURL (full-URL early return) and drop the tenant basePath.
      host: isNative() ? MatrixEndpoint.getMatrixEndpoint() : undefined,
    });
    setAnalyticsClient(unifyAppsClient);

    return () => {
      stopAnalyticsClient(unifyAppsClient);
      setAnalyticsClient(undefined);
    };
  }, [isEnabled, config, getTrackingDataFn, fetchInstance]);

  return (
    <AnalyticsContext.Provider value={analyticsClient}>
      <AnalyticsProviderContent>{children}</AnalyticsProviderContent>
    </AnalyticsContext.Provider>
  );
}

export { AnalyticsProvider };
